/**
 * `RuntimeState.progression` — where the player is and how far they have come
 * (architecture.md §3.2).
 *
 * @remarks
 * `activeChapterId` is optional at v0 depth: the vertical slice has no
 * chapter gates (decision D17), and a pack without chapters simply never sets
 * it. `turnCount` advances only when an action consumes time
 * (architecture.md §7.2 step 5).
 *
 * @packageDocumentation
 */

import { z } from 'zod';

import { chapterIdSchema, roomIdSchema } from '../identifiers.js';
import { nonNegativeIntegerSchema } from '../primitives.js';
import { narrativeRuntimeStateSchema } from './narrative-runtime-state.js';
import { schedulerStateSchema } from './scheduler-state.js';
import { worldStateSchema } from './world-state.js';

/**
 * D142: the world as it stood when the `checkpoint` effect last fired — the
 * three sub-states a restore reads (which parts it KEEPS from the present is
 * the restore rule's business, not the snapshot's) and where and when it was
 * taken. Stored whole rather than as a hand-picked subset so the checkpoint is
 * an honest photograph and the rule lives in one place.
 */
export const checkpointStateSchema = z
	.strictObject({
		activeRoomId: roomIdSchema,
		narrative: narrativeRuntimeStateSchema,
		scheduler: schedulerStateSchema,
		takenAtTurn: nonNegativeIntegerSchema,
		world: worldStateSchema,
	})
	.readonly();

/** The checkpoint — see {@link checkpointStateSchema}. */
export type CheckpointState = z.infer<typeof checkpointStateSchema>;

/** Active chapter/room, visited and discovered-secret rooms, and the turn counter. */
export const progressionStateSchema = z
	.strictObject({
		activeChapterId: chapterIdSchema.optional(),
		activeRoomId: roomIdSchema,
		/** D142: absent until the first `checkpoint` effect fires. */
		checkpoint: checkpointStateSchema.optional(),
		discoveredSecretRoomIds: z.array(roomIdSchema).readonly(),
		turnCount: nonNegativeIntegerSchema,
		visitedRoomIds: z.array(roomIdSchema).readonly(),
	})
	.readonly();

/** The `progression` sub-state — see {@link progressionStateSchema}. */
export type ProgressionState = z.infer<typeof progressionStateSchema>;
